/**
 * PulseDashboard — the new high-energy landing-page block.
 *
 * Structure (per /app/design_guidelines.json):
 *   1. Hero head: huge editorial headline + season chip + freshness
 *   2. AlertBanner (only if there are urgent alerts)
 *   3. 6-tile Bento grid (Stress, Wildfire, Bees, Frost, Market/NDVI mix, Moisture)
 *   4. Event-Stream (vertical chronological feed)
 *   5. Daily-recommendation card (editorial warmth)
 *
 * All sections are wired to /api/pulse/now which is refreshed every 5 min.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Fire, FlowerTulip as Bee, Snowflake, Drop, TrendUp, TrendDown, Minus, Newspaper,
  Leaf, ThermometerSimple, Compass, BookOpen, ArrowRight, Lightning,
  Plant as Seedling, Tree, Sun,
} from "@phosphor-icons/react";

import AnimatedNumber from "./AnimatedNumber";
import StatusTile from "./StatusTile";
import ClimateChangeTile from "./ClimateChangeTile";
import { useFreshness } from "@/hooks/useFreshness";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const ICON_BY_NAME = {
  fire: Fire, bee: Bee, snowflake: Snowflake, drop: Drop, trendUp: TrendUp,
  trendDown: TrendDown, newspaper: Newspaper, leaf: Leaf, sun: Sun,
  thermometer: ThermometerSimple, compass: Compass, book: BookOpen,
  seedling: Seedling, tree: Tree, wheat: Seedling,
};

const Icon = ({ name, ...props }) => {
  const C = ICON_BY_NAME[name] || Lightning;
  return <C {...props} />;
};

function fetchPulse() {
  return axios.get(`${API_BASE}/api/pulse/now`).then((r) => r.data);
}

function AlertBanner({ pulse }) {
  const a = pulse?.alerts;
  if (!a) return null;
  const items = [];
  if (a.frost_count >= 3) {
    items.push({ level: "critical", text: `Frostgefahr in ${a.frost_count} Regionen — junge Triebe schützen.` });
  } else if (a.frost_count > 0) {
    items.push({ level: "warning", text: `Frost-Risiko: ${a.frost_count} Region${a.frost_count > 1 ? "en" : ""} unter 0 °C in 72 h.` });
  }
  if (a.heat_count >= 3) {
    items.push({ level: "critical", text: `Hitzewelle in ${a.heat_count} Regionen — > 30 °C in 72 h.` });
  } else if (a.heat_count > 0) {
    items.push({ level: "warning", text: `Hitze-Risiko: ${a.heat_count} Region${a.heat_count > 1 ? "en" : ""} über 30 °C in 72 h.` });
  }
  if ((a.fire_risk || "").toLowerCase() === "hoch") {
    items.push({ level: "critical", text: `Erhöhtes Waldbrand-Risiko europaweit — vorsichtig mit offenem Feuer.` });
  } else if ((a.fire_risk || "").toLowerCase() === "erhöht") {
    items.push({ level: "warning", text: `Waldbrand-Aktivität moderat erhöht — beachte regionale Verbote.` });
  }
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="alert-stack">
      {items.map((it, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: i * 0.08 }}
          className={[
            "p-4 flex items-center gap-3 border-l-8 border-white/15",
            it.level === "critical"
              ? "bg-[#D13C30] text-white font-bold uppercase tracking-wide"
              : "bg-[#FFC24B] text-white font-semibold",
          ].join(" ")}
          data-testid={`alert-${it.level}`}
        >
          <motion.span
            className="w-2.5 h-2.5 rounded-full bg-current"
            animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          />
          <span className="text-sm">{it.text}</span>
        </motion.div>
      ))}
    </div>
  );
}

function StressTile({ pulse }) {
  const top = pulse.top_stress;
  if (!top) return null;
  return (
    <StatusTile
      label="Stress-Hotspot"
      fetchedAt={pulse.fetched_at}
      accentColor="#FF5500"
      to="/satellite"
      className="min-h-[200px]"
      testid="tile-stress"
    >
      <div className="flex items-center gap-2 text-white/60 text-xs font-mono uppercase tracking-widest">
        <Fire size={14} weight="fill" className="text-[#FF7A45]" />
        Niedrigster NDVI · jetzt
      </div>
      <div className="bb-metric text-white text-6xl md:text-7xl">
        <AnimatedNumber
          value={(top.ndvi || 0) * 100}
          format={(v) => `0.${Math.round(v).toString().padStart(2, "0")}`}
        />
      </div>
      <div className="text-white text-xl font-semibold leading-tight">{top.label}</div>
      <div className="text-white/60 text-sm">
        {top.country} · {top.crop || "Mischkultur"}
      </div>
      <div className="mt-auto inline-flex items-center gap-1 text-xs text-[#FF7A45] font-medium group">
        Karte öffnen <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
      </div>
    </StatusTile>
  );
}

function WildfireTile({ pulse }) {
  const w = pulse.wildfires;
  if (!w) {
    return (
      <StatusTile
        label="Waldbrand-Monitor"
        fetchedAt={pulse.fetched_at}
        accentColor="#FF5500"
        className="min-h-[200px]"
        testid="tile-wildfire"
      >
        <div className="text-white/60 text-sm">NASA FIRMS · derzeit nicht erreichbar</div>
      </StatusTile>
    );
  }
  const riskTone = {
    hoch: "text-[#FF6A5A]",
    erhöht: "text-[#FFC24B]",
    niedrig: "text-[#9FE8C4]",
  }[w.risk_level] || "text-white";

  return (
    <StatusTile
      label="Waldbrand-Monitor"
      fetchedAt={w.fetched_at || pulse.fetched_at}
      accentColor="#FF5500"
      className="min-h-[200px] cc-beam cc-beam-fire"
      testid="tile-wildfire"
    >
      <div className="flex items-center gap-2 text-white/60 text-xs font-mono uppercase tracking-widest">
        <Fire size={14} weight="fill" className="text-[#FF6A5A]" />
        Aktive Hot-Spots · 24 h
      </div>
      <div className="grid grid-cols-3 gap-3 mt-1">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-white/50 font-mono">Welt</div>
          <div className="bb-metric text-white text-2xl md:text-3xl">
            <AnimatedNumber value={w.global_24h || 0} />
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-white/50 font-mono">Europa</div>
          <div className="bb-metric text-white text-2xl md:text-3xl">
            <AnimatedNumber value={w.europe_24h || 0} />
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-white/50 font-mono">DE/AT</div>
          <div className="bb-metric text-[#FF7A45] text-2xl md:text-3xl">
            <AnimatedNumber value={w.deat_24h || 0} />
          </div>
        </div>
      </div>
      <div className={`mt-2 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest ${riskTone}`}>
        Risiko: {w.risk_level}
        <motion.span
          className="w-2 h-2 rounded-full bg-current"
          animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        />
      </div>
      <div className="text-[10px] text-white/40 font-mono mt-auto">
        NASA FIRMS · VIIRS S-NPP · max. {w.max_frp} MW
      </div>
    </StatusTile>
  );
}

function BeesTile({ pulse }) {
  const b = pulse.bees;
  if (!b) return null;
  return (
    <StatusTile
      label="Bienenflug"
      fetchedAt={pulse.fetched_at}
      accentColor="#FFD700"
      className=""
      testid="tile-bees"
    >
      <div className="flex items-center gap-3">
        <Bee size={36} weight={b.active ? "fill" : "regular"}
          className={b.active ? "text-[#FFC24B]" : "text-white/30"} />
        <div>
          <div className={`text-2xl font-bold ${b.active ? "text-white" : "text-white/50"}`}>
            {b.active ? "aktiv" : "ruhend"}
          </div>
          <div className="text-xs text-white/60 font-mono">
            Ø {b.avg_temp.toFixed(1)} °C · DE/AT
          </div>
        </div>
      </div>
    </StatusTile>
  );
}

function FrostTile({ pulse }) {
  const fc = pulse.alerts.frost_count;
  const hc = pulse.alerts.heat_count;
  const isFrost = fc > 0;
  const isHeat = hc > 0;
  return (
    <StatusTile
      label={isHeat && !isFrost ? "Hitze-Risiko" : "Frost-Risiko"}
      fetchedAt={pulse.fetched_at}
      accentColor={isFrost ? "#8B7CF6" : isHeat ? "#FF5500" : "#00FA9A"}
      className=""
      testid="tile-frost"
    >
      <div className="flex items-center gap-3">
        {isHeat && !isFrost ? (
          <Sun size={36} weight="fill" className="text-[#FF7A45]" />
        ) : (
          <Snowflake size={36} weight={isFrost ? "fill" : "regular"}
            className={isFrost ? "text-[#A99AFF]" : "text-[#9FE8C4]/50"} />
        )}
        <div>
          <div className="text-2xl font-bold text-white">
            <AnimatedNumber value={isHeat ? hc : fc} /> Region{(isHeat ? hc : fc) === 1 ? "" : "en"}
          </div>
          <div className="text-xs text-white/60 font-mono">
            {isFrost ? "< 0 °C in 72 h" : isHeat ? "> 30 °C in 72 h" : "Alles entspannt"}
          </div>
        </div>
      </div>
    </StatusTile>
  );
}

function MoistureTile({ pulse }) {
  const m = pulse.moisture || {};
  const pct = m.avg_pct;
  const has = pct != null;
  return (
    <StatusTile
      label="Bodenfeuchte"
      fetchedAt={pulse.fetched_at}
      accentColor="#8B7CF6"
      className=""
      testid="tile-moisture"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-3xl font-bold text-white leading-none">
            {has ? (
              <>
                <AnimatedNumber value={pct} format={(v) => `${v.toFixed(1)}%`} />
              </>
            ) : "—"}
          </div>
          <div className="text-xs text-white/60 font-mono mt-1">
            Ø über {m.n_regions || 0} Regionen · 0-10 cm
          </div>
        </div>
        <div className="flex-1 h-8 bg-[#161616] rounded-sm overflow-hidden border border-white/15">
          <motion.div
            className="h-full bg-gradient-to-r from-[#FFC24B] via-[#3D2A78] to-[#2C402B]"
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, Math.max(5, pct || 0))}%` }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          />
        </div>
      </div>
    </StatusTile>
  );
}

function VitalTile({ pulse }) {
  const v = pulse.top_vital;
  if (!v) return null;
  return (
    <StatusTile
      label="Vital-Highlight"
      fetchedAt={pulse.fetched_at}
      accentColor="#00FA9A"
      to="/satellite"
      className=""
      testid="tile-vital"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-[#9FE8C4] text-xs font-mono uppercase tracking-widest">
            <TrendUp size={14} weight="fill" />
            Höchster NDVI
          </div>
          <div className="text-3xl font-bold text-white leading-none mt-1">
            <AnimatedNumber
              value={(v.ndvi || 0) * 100}
              format={(val) => `0.${Math.round(val).toString().padStart(2, "0")}`}
            />
          </div>
          <div className="text-sm text-white mt-1">{v.label}</div>
        </div>
        <div className="text-[#9FE8C4]/60 hidden sm:block">
          <Leaf size={48} weight="duotone" />
        </div>
      </div>
    </StatusTile>
  );
}

function RecommendationCard({ pulse }) {
  const r = pulse.recommendation;
  if (!r) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative bg-white/10 p-8 md:p-12 border border-white/15 overflow-hidden"
      data-testid="recommendation-card"
    >
      <div className="absolute -top-8 -right-8 opacity-10">
        <Icon name={r.icon} size={220} weight="duotone" className="text-white" />
      </div>
      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-mono">
          Heute ist ein guter Tag für
        </div>
        <h3 className="mt-3 font-display text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-[0.95] tracking-tighter">
          {r.verb}.
        </h3>
        <p className="mt-4 text-base md:text-lg text-white/80 max-w-2xl">
          {r.detail}
        </p>
      </div>
    </motion.div>
  );
}

function EventStream({ pulse }) {
  const items = pulse.stream || [];
  if (items.length === 0) return null;
  return (
    <div
      className="bg-[#121212] border border-white/15 p-6 md:p-8"
      data-testid="event-stream"
    >
      <div className="flex items-baseline justify-between mb-6">
        <h3 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight">Was passiert gerade?</h3>
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono flex items-center gap-2">
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-[#FF5500]"
            animate={{ scale: [1, 1.6, 1], opacity: [1, 0.4, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          />
          live · {pulse.stream.length} Ereignisse
        </span>
      </div>
      <ol className="relative border-l-2 border-white/15 ml-2 pl-6 space-y-7">
        {items.map((e, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.4, delay: i * 0.04 }}
            className="relative"
            data-testid={`stream-item-${i}`}
          >
            <span className="absolute -left-[33px] top-1.5 flex items-center justify-center w-5 h-5 bg-[#121212] border border-white/15 rounded-full">
              <Icon name={e.icon} size={10} weight="fill" className="text-white" />
            </span>
            <StreamItemBody event={e} />
          </motion.li>
        ))}
      </ol>
    </div>
  );
}

function StreamItemBody({ event }) {
  const fresh = useFreshness(event.ts);
  return (
    <div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-mono">
          {event.type === "post" ? (event.topic_label || "Bodenblick") : event.type.toUpperCase()}
        </span>
        <span className="text-[10px] text-white/40 font-mono">{fresh}</span>
      </div>
      <div className="text-base font-semibold text-white mt-0.5 leading-snug">
        {event.headline}
      </div>
      {event.detail ? (
        <div className="text-sm text-white/70 mt-1 max-w-2xl">{event.detail}</div>
      ) : null}
      {event.type === "post" && event.id ? (
        <a
          href={`/heute#${event.id}`}
          className="inline-flex items-center gap-1 mt-1 text-xs text-[#FF7A45] hover:underline"
        >
          Beitrag öffnen <ArrowRight size={11} />
        </a>
      ) : null}
    </div>
  );
}

export default function PulseDashboard() {
  const [pulse, setPulse] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const refresh = async () => {
      try {
        const d = await fetchPulse();
        if (mounted) {
          setPulse(d);
          setError(null);
        }
      } catch (e) {
        if (mounted) setError(e?.message || "fetch failed");
      }
    };
    refresh();
    const id = setInterval(refresh, 5 * 60 * 1000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (error && !pulse) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-white/60">
        Live-Daten kurz nicht verfügbar — wir versuchen es gleich erneut.
      </div>
    );
  }
  if (!pulse) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-white/10 w-1/2" />
          <div className="h-64 bg-white/10" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Live-Ticker is rendered separately at the very top */}
      <div className="space-y-10" data-testid="pulse-dashboard">
        <AlertBanner pulse={pulse} />

        <div
          className="sm:hidden flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono text-white/40 px-1"
          data-testid="pulse-swipe-hint"
        >
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-[#FF5500]"
            animate={{ scale: [1, 1.6, 1], opacity: [1, 0.5, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          />
          Swipe für weitere Kacheln →
        </div>

        <div
          className="
            flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4
            overflow-x-auto sm:overflow-visible snap-x snap-mandatory
            -mx-4 sm:mx-0 px-4 sm:px-0
            pb-2 sm:pb-0
            [&>*]:snap-start [&>*]:shrink-0 [&>*]:w-[82%] sm:[&>*]:w-auto
            scrollbar-thin
          "
          data-testid="pulse-tile-carousel"
        >
          <StressTile pulse={pulse} />
          <WildfireTile pulse={pulse} />
          <BeesTile pulse={pulse} />
          <FrostTile pulse={pulse} />
          <VitalTile pulse={pulse} />
          <MoistureTile pulse={pulse} />
          <ClimateChangeTile />
        </div>

        <RecommendationCard pulse={pulse} />

        <EventStream pulse={pulse} />
      </div>
    </>
  );
}

export { fetchPulse };
